/*
* TextContents edit view for authenticated users
*/

import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import ReactImageFallback from 'react-image-fallback';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import Auth from '../auth/auth';
import Branding from '../branding/branding';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import * as UserComponents from '../common/user-components';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as LK from '../config/apilookupkeys';
import { AuthConfig } from '../config/authconfiguration';
import * as DetailFormCommon from '../inventory/detail-form/common';
import { PortalRoles } from '../invite/create-invite';
import * as ReduxQueries from '../queries';
import { UserUtilities } from '../user/user-utilities';

export const DefaultNewUserRoleIds: Array<string> = ['5b5c9e45171a7d764a7241c3']; // Employee role

export interface ITextContentEditProps extends AppComp.IAppComponentProps {
    userDetail?: any;
    userObjId?: any;
    textContentObjId?: any;
    updateUser?: any;
    textContent?: any;
    forceRequest?: any;
    newUser?: any;
    navigateTo?: any;
    userResume?: any;
    regions?: any;
    updateLookupObj?: any;
    roles?: any;
    users?: any;
    portalInvites?: any;
}

export interface ITextContentEditState extends AppComp.IAppComponentState {
    formChanges?: any;
    showModalResetPassword?: boolean;
    showModalNewUser?: boolean;
}

class EditTextContent extends AppComp.AppComponent<ITextContentEditProps, ITextContentEditState> {
    public originalFormDataCache = {};

    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: ITextContentEditProps) {
        super(props, 'app.text-content.');

        this.state = {
            formChanges: {},
            showModalResetPassword: false,
            showModalNewUser: false,
        };
    }

    public componentDidMount() {
        const targetUserObjId = Formatters.getStr(this.props, 'userObjId');

        const enforce = this.enforceAdminOnly();

        if (enforce) {
            this.props.navigateTo(NavigationLinks.userDashboard());
        }
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // TRANSFORMS

    // HANDLERS
    public handleShowModalResetPassword(e: any, formData: any) {
        this.setState({ showModalResetPassword: true });
    }

    public handleCloseModalResetPassword() {
        this.setState({ showModalResetPassword: false });
    }

    public handleSaveButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { obj: _.merge({}, _.pick(this.props.textContent, ['name', '_id']), this.state.formChanges) },
            { tableName: LK.LookupKeys.TextContents },
            { textContentKey: Formatters.getStr(this.props, 'textContent.textContentKey') },
            this.state.formChanges
        );

        // console.log('handleSaveButton changeData', changeData);

        this.props.updateLookupObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.setState({ formChanges: {} });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleSearchUsersButton(e: any) {
        this.props.navigateTo(NavigationLinks.userSearch());
    }

    public handleViewProfileButton(e: any) {
        const ud = Formatters.getStr(this.props, 'userDetail._id');
        this.props.navigateTo(NavigationLinks.userDetailObjId(ud));
    }

    public handleCreateNewUserButton(e: any) {
        this.props.navigateTo(NavigationLinks.userAdd());
    }

    public handleShowModalNewUser(e: any, formData: any) {
        let formChanges = _.cloneDeep(this.state.formChanges);
        const newUser = this.getDefaultNewUserObject(_.get(formData, 'userRoleOptions', []));
        _.set(formChanges, 'newUser', newUser);

        this.setState({ formChanges: formChanges, showModalNewUser: true });
    }

    public handleCloseModalNewUser() {
        this.setState({ showModalNewUser: false });
    }

    public handleNewUserButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { user: _.pick(this.props.userDetail, ['username', '_id']) },
            _.pick(this.state.formChanges, ['newUser']),
        );

        this.props.newUser(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                this.setState({ showModalNewUser: false });

                // FORCE RELOAD
                const newUserObjId = _.get(response, 'body._id');

                this.props.navigateTo(NavigationLinks.userEditObjId(newUserObjId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        // console.log('handleForminputchange', { e: e, x: x });

        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    public handleFormRoleCheckboxChange(e: any, x: any) {
        const roleObjId = Formatters.getStr(e, 'target.props.name');
        const roleNewAssigned = _.get(e, 'value') ? true : false;

        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, textContent, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, textContent);

        let filteredRolesAdd = _.reject(_.cloneDeep(_.get(formData, 'rolesAdd', [])), (o) => {
            const match = (String(o) === String(roleObjId)) ? true : false;
            return match;
        });
        let filteredRolesDelete = _.reject(_.cloneDeep(_.get(formData, 'rolesDelete', [])), (o) => {
            const match = (String(o) === String(roleObjId)) ? true : false;
            return match;
        });

        let userRolesChanges = _.stubArray();
        if (!roleNewAssigned) {
            // Delete role
            filteredRolesDelete.push(roleObjId);
            userRolesChanges.push({ fieldName: 'rolesAdd', newValue: _.cloneDeep(filteredRolesAdd) });
            userRolesChanges.push({ fieldName: 'rolesDelete', newValue: _.cloneDeep(filteredRolesDelete) });
        } else {
            // Add role
            filteredRolesAdd.push(roleObjId);
            userRolesChanges.push({ fieldName: 'rolesAdd', newValue: _.cloneDeep(filteredRolesAdd) });
            userRolesChanges.push({ fieldName: 'rolesDelete', newValue: _.cloneDeep(filteredRolesDelete) });
        }

        if (_.size(userRolesChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            userRolesChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleFormNewUserRoleChange(e: any, x: any) {
        const newVal = _.get(e, 'target.value');

        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, textContent, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, textContent);

        let cloneFormChanges = _.cloneDeep(this.state.formChanges);
        _.set(cloneFormChanges, 'newUser.userRolesAssigned', newVal);

        this.setState({ formChanges: cloneFormChanges });
    }

    public handleFormRegionCheckboxChange(e: any, x: any) {
        const regionObjId = Formatters.getStr(e, 'target.props.name');
        const newlyAssigned = _.get(e, 'value') ? true : false;

        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, textContent, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, textContent);

        let filteredAdd = _.reject(_.cloneDeep(_.get(formData, 'regionsAdd', [])), (o) => {
            const match = (String(o) === String(regionObjId)) ? true : false;
            return match;
        });
        let filteredDelete = _.reject(_.cloneDeep(_.get(formData, 'regionsDelete', [])), (o) => {
            const match = (String(o) === String(regionObjId)) ? true : false;
            return match;
        });

        let userChanges = _.stubArray();
        if (!newlyAssigned) {
            // Delete role
            filteredDelete.push(regionObjId);
            userChanges.push({ fieldName: 'regionsAdd', newValue: _.cloneDeep(filteredAdd) });
            userChanges.push({ fieldName: 'regionsDelete', newValue: _.cloneDeep(filteredDelete) });
        } else {
            // Add role
            filteredAdd.push(regionObjId);
            userChanges.push({ fieldName: 'regionsAdd', newValue: _.cloneDeep(filteredAdd) });
            userChanges.push({ fieldName: 'regionsDelete', newValue: _.cloneDeep(filteredDelete) });
        }

        if (_.size(userChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            userChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleFormNewUserRegionChange(e: any, x: any) {
        const newVal = _.get(e, 'target.value');

        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, textContent, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, textContent);

        let cloneFormChanges = _.cloneDeep(this.state.formChanges);
        _.set(cloneFormChanges, 'newUser.userRegionsAssigned', newVal);

        this.setState({ formChanges: cloneFormChanges });
    }

    public handleFormResumeInputChange(e: any) {
        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, textContent, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, textContent);

        const prevResumeHtml = Formatters.getStr(formData, 'userResume.resume');
        const newResumeHtml = String(e);

        let userResumeChanges = _.stubArray();
        if (prevResumeHtml !== newResumeHtml) {
            userResumeChanges.push({ fieldName: 'userResume.resume', newValue: newResumeHtml });
        }

        if (_.size(userResumeChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            userResumeChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleFormHtmlInputChange(content: any, delta: any, source: any, editor: any, fieldName: any) {
        if (source === 'api' || !fieldName) { return; }
        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, textContent, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, textContent);

        // console.log('handleFormHtmlInputChange', { content, delta, source, editor, fieldName });

        const prevFieldHtml = Formatters.getStr(formData, fieldName);
        const newHtml = String(content);

        let modelChanges = _.stubArray();
        if (prevFieldHtml !== newHtml) {
            modelChanges.push({ fieldName: fieldName, newValue: newHtml });
        }

        if (_.size(modelChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            modelChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    // HELPERS
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                case 'email':
                case 'password':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            // if (String(newValue) !== String(origVal)) {
            // New value
            _.set(formChangesCopy, fieldName, newValue);
            _.set(formChangesResult, fieldName, newValue);
            applyChange = true;
            // } else {
            //     // Same as orig val
            //     if (_.has(formChangesCopy, fieldName)) {
            //         _.unset(formChangesCopy, fieldName);
            //         _.set(formChangesResult, fieldName, newValue);
            //         applyChange = true;
            //     }
            // }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public getFormData(userIn: any, userResume: any, roles: any, users: any, portalInvites: any, regions: any, textContentIn: any) {
        const user = _.merge({}, userIn, this.state.formChanges);
        const textContent = _.merge({}, textContentIn, this.state.formChanges);

        const userRoleOptions = (_.size(roles)) ?
            roles.map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'name'),
                assigned: false,
            })) : [];

        const userRegionOptions = (_.size(regions)) ?
            regions.map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'name'),
                assigned: false,
            })) : [];

        let newUser = _.get(user, 'newUser', this.getDefaultNewUserObject(userRoleOptions));

        const contents = _.get(textContent, 'contents', {});

        let result = {
            name: Formatters.getStr(textContent, 'name'),
            textContentKey: Formatters.getStr(textContent, 'textContentKey'),
            contents: {
                contractHeaderPage1: Formatters.getStr(contents, 'contractHeaderPage1'),
                contractHeaderPage2: Formatters.getStr(contents, 'contractHeaderPage2'),
                contractHeaderArticle2: Formatters.getStr(contents, 'contractHeaderArticle2'),
                contractHeaderArticle3: Formatters.getStr(contents, 'contractHeaderArticle3'),
                contractHeaderArticle4: Formatters.getStr(contents, 'contractHeaderArticle4'),
                letterIntro: Formatters.getStr(contents, 'letterIntro'),
                letterConclusion: Formatters.getStr(contents, 'letterConclusion'),
                letterSignature: Formatters.getStr(contents, 'letterSignature'),
                contractSignaturesArea: Formatters.getStr(contents, 'contractSignaturesArea'),
                contractItemsArticle1Page1: _.get(contents, 'contractItemsArticle1Page1', []),
                contractItemsArticle1Page2: _.get(contents, 'contractItemsArticle1Page2', []),
                contractItemsArticle2Page2: _.get(contents, 'contractItemsArticle2Page2', []),
                contractItemsArticle3Page2: _.get(contents, 'contractItemsArticle3Page2', []),
                contractItemsArticle4Page2: _.get(contents, 'contractItemsArticle4Page2', []),
            },

            username: Formatters.getStr(user, 'username'),
            contact: _.get(user, 'contact'),
            email: Formatters.getStr(user, 'email'),
            inactive: _.get(user, 'inactive'),
            lastLogin: _.get(user, 'lastLogin'),
            locked: _.get(user, 'locked'),
            signaturePictureFilename: Formatters.getStr(user, 'signaturePictureFilename'),
            profileSettings: {
                hideReplacementCosts: Formatters.getBool(user, 'profileSettings.hideReplacementCosts'),
            },

            portalInvites: (_.size(portalInvites)) ? portalInvites : [],

            roles: _.get(user, 'roles', []),
            rolesExisting: _.map(_.get(userIn, 'roles', []), 'roleObjectId'),
            rolesAdd: _.get(user, 'rolesAdd', []),
            rolesDelete: _.get(user, 'rolesDelete', []),

            regions: _.get(user, 'regions', []),
            regionsExisting: _.map(_.get(userIn, 'regions', []), 'regionObjectId'),
            regionsAdd: _.get(user, 'regionsAdd', []),
            regionsDelete: _.get(user, 'regionsDelete', []),

            resetPassword: Formatters.getStr(user, 'resetPassword'),
            resetPasswordConfirm: Formatters.getStr(user, 'resetPasswordConfirm'),
            passwordChangeRequired: Formatters.getBool(user, 'passwordChangeRequired'),

            newUser: newUser,

            userResume: userResume,
            userRoleOptions: (_.size(userRoleOptions)) ? userRoleOptions : [],
            userRegionOptions: (_.size(userRegionOptions)) ? userRegionOptions : [],

            users: (_.size(users)) ? users : [],
        };

        if ((_.size(result.rolesExisting) || _.size(result.rolesAdd) || _.size(result.rolesDelete)) && _.size(result.userRoleOptions)) {
            _.forEach(result.userRoleOptions, (o) => {
                const roleObjId = Formatters.getStr(o, 'value');

                const isExisting = (_.includes(result.rolesExisting, roleObjId)) ? true : false;
                const isAdded = (_.includes(result.rolesAdd, roleObjId)) ? true : false;
                const isDeleted = (_.includes(result.rolesDelete, roleObjId)) ? true : false;

                const isAssigned = ((isExisting || isAdded) && !isDeleted) ? true : false;
                // console.log(`userRoleOptions.map value ${o.value} ${o.text}: isAssigned ${isAssigned} - ((isExisting ${isExisting} || isAdded ${isAdded})) && !isDeleted ${isDeleted}`);

                if (o.assigned !== isAssigned) {
                    // console.log(`userRoleOptions.map changing value ${roleObjId} ${o.text}: assigned from ${o.assigned} to ${isAssigned}`);
                    _.set(o, 'assigned', isAssigned);
                }
            });
        }

        if ((_.size(result.regionsExisting) || _.size(result.regionsAdd) || _.size(result.regionsDelete)) && _.size(result.userRegionOptions)) {
            _.forEach(result.userRegionOptions, (o) => {
                const regionObjId = Formatters.getStr(o, 'value');

                const isExisting = (_.includes(result.regionsExisting, regionObjId)) ? true : false;
                const isAdded = (_.includes(result.regionsAdd, regionObjId)) ? true : false;
                const isDeleted = (_.includes(result.regionsDelete, regionObjId)) ? true : false;

                const isAssigned = ((isExisting || isAdded) && !isDeleted) ? true : false;

                if (o.assigned !== isAssigned) {
                    _.set(o, 'assigned', isAssigned);
                }
            });
        }

        return _.merge({}, result, this.state.formChanges);
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        // Props values
        const pa = Formatters.getStr(this.props, 'textContentObjId');

        // Cached values
        const ca = Formatters.getStr(this.originalFormDataCache, 'textContentObjId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    public getDefaultNewUserObject(userRoleOptions: any) {
        return _.cloneDeep({
            username: '',
            password: '',
            passwordConfirm: '',
            passwordChangeRequired: false,
            email: '',
            firstName: '',
            lastName: '',
            title: '',
            inactive: false,
            locked: false,
            profileSettings: {
                hideReplacementCosts: false,
            },
            userRolesAssigned: _.filter(userRoleOptions, (o) => _.includes(DefaultNewUserRoleIds, _.get(o, 'value', []))),
            // userRegionsAssigned: _.filter(userRegionOptions, (o) => _.includes(DefaultNewUserRegionIds, _.get(o, 'value', [])))
        });
    }

    // RENDERERS
    public renderHeaderButtonToolbar = () => {
        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')} ${Formatters.plural(this.appTextCommon('buttons.change'), this.getFormChangesCount(), false)}`,
            };
        }

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={4} xl={3}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button {...saveChangesButtonProps} onClick={e => { this.handleSaveButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>
                                </ButtonGroup>

                                <ButtonGroup>
                                    {/* <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleShowModalNewUser(e, formData); }}> */}
                                    <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleCreateNewUserButton(e); }}>
                                        <i className="glyphicon glyphicon-plus" /> {this.appText('new-user')}
                                    </Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>

                        <Col xs={6}>
                            <ButtonToolbar>
                                <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleViewProfileButton(e); }}>
                                    <i className="fa fa-user" /> {this.appText('view-profile')}
                                </Button>
                                <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleSearchUsersButton(e); }}>
                                    <i className="fa fa-search" /> {this.appText('search-users')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderSectionUserContactDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            // onChange: (n, v) => { handleFormInputChange(n, v); },
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('contact-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('contact-details')}>
                                <KendoWrappers.KendoTextInputDebounced
                                    name="contact.firstName"
                                    label={this.appText('contact-first-name')}
                                    value={Formatters.getStr(formData, 'contact.firstName')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoTextInputDebounced
                                    name="contact.lastName"
                                    label={this.appText('contact-last-name')}
                                    value={Formatters.getStr(formData, 'contact.lastName')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoTextInputDebounced
                                    name="contact.title"
                                    label={this.appText('contact-title')}
                                    value={Formatters.getStr(formData, 'contact.title')}
                                    {...inputPropsFullWidthTextDebounced}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionTextContentDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7, colLabelXl: 2, };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            // className: 'inventory-detail-input-full-width',
        });

        const inputPropsHtmlEditor = _.merge({}, defaultInputProps, {
            // onChange: (content, delta, source, editor, fn) => { this.handleFormHtmlInputChange(content, delta, source, editor, fn); },
            colLabelXs: 12,
            colInputXs: 12,
            className: 'text-content-quill-container', // 'inventory-detail-input-full-width',
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('bid-letter.title')}>
                <Row>
                    <Col xs={12}>
                        <div className="text-content-edit-form">
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('bid-letter.title')}>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_bid-letter-name"
                                    label={this.appText('bid-letter.name')}
                                    value={Formatters.getStr(formData, 'name')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_bid-letter-text-content-key"
                                    label={this.appText('bid-letter.text-content-key')}
                                    value={Formatters.getStr(formData, 'textContentKey')}
                                    {...inputPropsFullWidthTextDebounced}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>

                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('bid-letter.letter-intro')}>
                                <KendoWrappers.KendoHtmlEditor
                                    name="letterIntro"
                                    label=""
                                    value={Formatters.getStr(formData, 'contents.letterIntro')}
                                    onChange={(content, delta, source, editor) => { this.handleFormHtmlInputChange(content, delta, source, editor, 'contents.letterIntro'); }}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>

                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('bid-letter.letter-conclusion')}>
                                <KendoWrappers.KendoHtmlEditor
                                    name="letterConclusion"
                                    label=""
                                    value={Formatters.getStr(formData, 'contents.letterConclusion')}
                                    onChange={(content, delta, source, editor) => { this.handleFormHtmlInputChange(content, delta, source, editor, 'contents.letterConclusion'); }}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>

                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('bid-letter.letter-signature')}>
                                <KendoWrappers.KendoHtmlEditor
                                    name="letterSignature"
                                    label=""
                                    value={Formatters.getStr(formData, 'contents.letterSignature')}
                                    onChange={(content, delta, source, editor) => { this.handleFormHtmlInputChange(content, delta, source, editor, 'contents.letterSignature'); }}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>

                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('bid-letter.contract-header-page-1')}>
                                <KendoWrappers.KendoHtmlEditor
                                    name="contractHeaderPage1"
                                    label=""
                                    value={Formatters.getStr(formData, 'contents.contractHeaderPage1')}
                                    onChange={(content, delta, source, editor) => { this.handleFormHtmlInputChange(content, delta, source, editor, 'contents.contractHeaderPage1'); }}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>

                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('bid-letter.contract-header-page-2')}>
                                <KendoWrappers.KendoHtmlEditor
                                    name="contractHeaderPage2"
                                    label=""
                                    value={Formatters.getStr(formData, 'contents.contractHeaderPage2')}
                                    onChange={(content, delta, source, editor) => { this.handleFormHtmlInputChange(content, delta, source, editor, 'contents.contractHeaderPage2'); }}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>

                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('bid-letter.contract-signatures-area')}>
                                <KendoWrappers.KendoHtmlEditor
                                    name="contractSignaturesArea"
                                    label=""
                                    value={Formatters.getStr(formData, 'contents.contractSignaturesArea')}
                                    onChange={(content, delta, source, editor) => { this.handleFormHtmlInputChange(content, delta, source, editor, 'contents.contractSignaturesArea'); }}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserLoginDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('login-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('login-details')}>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_login-username"
                                    label={this.appText('login-username')}
                                    value={Formatters.getStr(formData, 'username')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoTextInputDebounced
                                    name="email"
                                    label={this.appText('login-email')}
                                    value={Formatters.getStr(formData, 'email')}
                                    type="email"
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_login-lastlogin"
                                    label={this.appText('login-last-login')}
                                    value={Formatters.formatDateLongDate(Formatters.getStr(formData, 'lastLogin'), this.appText('login-never'))}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_login-reset-password"
                                    label={this.appText('login-password')}
                                    value=""
                                    {...inputPropsFullWidthTextDebounced}
                                >
                                    <div className="edit-order-set-due-date-button-wrapper">
                                        <Button
                                            bsSize="xs"
                                            bsStyle="info"
                                            onClick={e => { this.handleShowModalResetPassword(e, formData); }}
                                        >
                                            <i className="fa fa-lock" /> {this.appText('reset-password')}
                                        </Button>
                                    </div>
                                </KendoWrappers.KendoDisabledInputHidden>

                                <KendoWrappers.KendoCheckbox
                                    name="passwordChangeRequired"
                                    label={this.appTextCommon('forms.user.password.must-change-password')}
                                    value={Formatters.getBool(formData, 'passwordChangeRequired')}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserProfileSettings(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsRoleCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormRoleCheckboxChange(n, v); },
            colInputXs: 7,
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('profile-settings')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('profile-settings')}>
                                <KendoWrappers.KendoCheckbox
                                    name="profileSettings.hideReplacementCosts"
                                    label={this.appText('hide-replacement-costs')}
                                    value={formData.profileSettings.hideReplacementCosts}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserPortalAccessDetails(formData: any) {
        const allPortalInvites = _.get(formData, 'portalInvites', []);

        // if (!_.size(allPortalInvites)) { return (<div />); }

        const auth = new Auth();
        const isStaff = (auth.isAdmin() || auth.isEmployee()) ? true : false;
        const isClient = auth.isClient();
        const userObjId = auth.getSessionUserObjectId();

        const showDeactivePortalInvites = (isStaff) ? true : false;

        const validPortalInvites: any = UserUtilities.GetValidPortalInvites(allPortalInvites);
        const deactivePortalInvites: any = UserUtilities.GetInvalidPortalInvites(allPortalInvites);

        // console.log('renderSectionUserPortalAccessDetails', {formData, allPortalInvites, validPortalInvites, deactivePortalInvites});

        // if (!_.size(validPortalInvites)) { return (<div />); }

        const filterAdminRoleInvites = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalAdmin.RoleName);
        const invitesAdmin = _.map(filterAdminRoleInvites, (o) => {
            const assocId = _.get(o, 'association.associationId', 0);
            // const assocLink = NavigationLinks.associationAnalysis(assocId);
            const assocLink = NavigationLinks.portalAssociationReport(assocId);

            return UserUtilities.BuildInviteLinkToAssociation(o, assocLink, true);
        });
        const portalAccessAdmin = (_.size(invitesAdmin)) ? (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('portal-role-admin')}>
                {invitesAdmin}
            </DetailFormCommon.InventoryDetailFormSection>
        ) : (<div />);

        const filterUserRoleInvites = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalUser.RoleName);
        const invitesUser = _.map(filterUserRoleInvites, (o) => {
            const assocId = _.get(o, 'association.associationId', 0);
            // const assocLink = NavigationLinks.associationAnalysis(assocId);
            const assocLink = NavigationLinks.portalAssociationReport(assocId);
            return UserUtilities.BuildInviteLinkToAssociation(o, assocLink, true);
        });
        const portalAccessUser = (_.size(invitesUser)) ? (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('portal-role-user')}>
                {invitesUser}
            </DetailFormCommon.InventoryDetailFormSection>
        ) : (<div />);

        // console.log('filterUser', {filterUser: filterUserRoleInvites, rolesUser: invitesUser});

        const invitesDeactive = _.map(deactivePortalInvites, (o) => {
            const assocId = _.get(o, 'association.associationId', 0);
            // const assocLink = NavigationLinks.associationAnalysis(assocId);
            const assocLink = NavigationLinks.portalAssociationReport(assocId);
            return UserUtilities.BuildInviteLinkToAssociation(o, assocLink, true);
        });
        const portalAccessDeactive = (showDeactivePortalInvites && _.size(invitesDeactive)) ? (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('portal-invite-deactive')}>
                {invitesDeactive}
            </DetailFormCommon.InventoryDetailFormSection>
        ) : (<div />);

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('portal-access')}>
                <Row>
                    <Col xs={12}>
                        <div className="user-edit-portal-invites">
                            {portalAccessAdmin}
                            {portalAccessUser}
                            {portalAccessDeactive}
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserSecurityDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsRoleCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormRoleCheckboxChange(n, v); },
            colInputXs: 7,
        });

        const inputPropsRegionCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormRegionCheckboxChange(n, v); },
            colInputXs: 7,
        });

        let roleCheckboxes = _.get(formData, 'userRoleOptions', []).map((c) => {
            const hasRoleAssigned = (_.get(c, 'assigned', false)) ? true : false;
            return (
                <KendoWrappers.KendoCheckbox
                    key={`role_${c.value}`}
                    name={c.value}
                    label={Formatters.getStr(c, 'text')}
                    value={hasRoleAssigned}
                    {...inputPropsRoleCheckbox}
                />
            );
        });

        let regionCheckboxes = _.get(formData, 'userRegionOptions', []).map((c) => {
            const hasRegionAssigned = (_.get(c, 'assigned', false)) ? true : false;
            return (
                <KendoWrappers.KendoCheckbox
                    key={`region_${c.value}`}
                    name={c.value}
                    label={Formatters.getStr(c, 'text')}
                    value={hasRegionAssigned}
                    {...inputPropsRegionCheckbox}
                />
            );
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('security-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-details')}>
                                <KendoWrappers.KendoCheckbox
                                    name="inactive"
                                    label={this.appText('security-inactive')}
                                    value={formData.inactive}
                                    {...inputProps}
                                />

                                <KendoWrappers.KendoCheckbox
                                    name="locked"
                                    label={this.appText('security-locked')}
                                    value={formData.locked}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-roles')}>
                                {roleCheckboxes}
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-regions')}>
                                {regionCheckboxes}
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserResumeDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 2,
            colInputXs: 2,
            className: 'inventory-detail-input-full-width',
        });

        const inputPropsHtmlEditor = _.merge({}, defaultInputProps, {
            onChange: (n) => { this.handleFormResumeInputChange(n); },
            colLabelXs: 12,
            colInputXs: 12,
            className: 'inventory-detail-input-full-width',
        });

        const userDetailRoles = _.get(formData, 'userRoleOptions', []);
        const isAdminOrEmployee = ((UserComponents.HasAssignedRole(AuthConfig.UserRoles.administrator, userDetailRoles)) || UserComponents.HasAssignedRole(AuthConfig.UserRoles.employee, userDetailRoles)) ? true : false;
        const isClient = ((UserComponents.HasAssignedRole(AuthConfig.UserRoles.client, userDetailRoles)) || UserComponents.HasAssignedRole(AuthConfig.UserRoles.clientReadOnly, userDetailRoles)) ? true : false;
        if (!isAdminOrEmployee && isClient) {
            return (<div />);
        }

        // Signature
        const signaturePath = Branding.getBrandUserSignaturePicture(Formatters.getStr(formData, 'signaturePictureFilename'), Branding.appBrandKeys.complexSolutions);
        const signatureNotFoundPath = Branding.getBrandUserSignaturePicture(Branding.appBrandImageFileNames.signatureNotFound, Branding.appBrandKeys.complexSolutions);

        const signatureImageObj = {
            src: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signaturePath, true, 'h_55,f_auto/'),
            fileName: signaturePath,
            fallbackSrc: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signatureNotFoundPath, true, 'h_55,f_auto/'),
        };

        const signaturePath2 = Branding.getBrandUserSignaturePicture(Formatters.getStr(formData, 'signaturePictureFilename'), Branding.appBrandKeys.appliedReserve);
        const signatureNotFoundPath2 = Branding.getBrandUserSignaturePicture(Branding.appBrandImageFileNames.signatureNotFound, Branding.appBrandKeys.appliedReserve);

        const signatureImageObj2 = {
            src: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signaturePath2, true, 'h_55,f_auto/'),
            fileName: signaturePath2,
            fallbackSrc: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signatureNotFoundPath2, true, 'h_55,f_auto/'),
        };

        const RenderSignaturePanel = ({ brandKey, sigImgObj, ...props }) => {
            return (
                <div className="inline-block resume-signature-picture-wrapper">
                    <div className="resume-signature-picture-brand-name">
                        {Branding.getBrandCompanyName(brandKey)}
                    </div>

                    <hr className="resume-signature-picture-hr" />

                    <div className="resume-signature-picture-image-wrapper text-center">
                        <ReactImageFallback
                            src={sigImgObj.src}
                            fallbackImage={sigImgObj.fallbackSrc}
                            className="resume-signature-picture-image"
                        />
                    </div>

                    <div className="resume-signature-picture-filename">
                        {sigImgObj.fileName}
                    </div>
                </div>
            );
        };

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('resume-details')}>
                <Row>
                    <Col xs={12}>
                        <div className="user-edit-resume-details">
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('resume-details')}>

                                <KendoWrappers.KendoTextInputDebounced
                                    name="userResume.permits[0].permitNumber"
                                    label={this.appText('resume-permit-number-nv')}
                                    value={Formatters.getStr(formData, 'userResume.permits[0].permitNumber')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_user-signature"
                                    label={this.appText('resume-signature')}
                                    value={[
                                        <RenderSignaturePanel key="sig-1" sigImgObj={signatureImageObj} brandKey={Branding.appBrandKeys.complexSolutions} />,
                                        <RenderSignaturePanel key="sig-2" sigImgObj={signatureImageObj2} brandKey={Branding.appBrandKeys.appliedReserve} />
                                    ]}
                                    {...inputPropsHtmlEditor}
                                />

                                <KendoWrappers.KendoHtmlEditor
                                    name="userResume.resume"
                                    label={this.appText('resume-text')}
                                    defaultValue={Formatters.getStr(formData, 'userResume.resume')}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderResetPasswordModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 6 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            // onChange: (n, v) => { handleFormInputChange(n, v); },
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        const submitButtonDisabled = (
            (_.size(Formatters.getStr(formData, 'resetPassword')) === 0 || _.size(Formatters.getStr(formData, 'resetPassword')) >= UserComponents.MinimumPasswordLength) &&
            Formatters.getStr(formData, 'resetPassword') === Formatters.getStr(formData, 'resetPasswordConfirm') &&
            (_.size(Formatters.getStr(formData, 'resetPasswordConfirm')) === 0 || _.size(Formatters.getStr(formData, 'resetPasswordConfirm')) >= UserComponents.MinimumPasswordLength)
        ) ? false : true;

        return (
            <Modal show={this.state.showModalResetPassword} onHide={() => this.handleCloseModalResetPassword()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.reset-password.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div>
                        <DetailFormCommon.InventoryDetailFormSection title="">
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_reset-password-username"
                                label={this.appText('login-username')}
                                value={Formatters.getStr(formData, 'username')}
                                {...inputPropsFullWidthTextDebounced}
                            />

                            <DetailFormCommon.InventoryDetailFormSectionSpacing />

                            <KendoWrappers.KendoTextInputDebounced
                                name="resetPassword"
                                label={this.appText('modals.reset-password.new-password')}
                                value={Formatters.getStr(formData, 'resetPassword')}
                                type="password"
                                {...inputPropsFullWidthTextDebounced}
                            />

                            <KendoWrappers.KendoTextInputDebounced
                                name="resetPasswordConfirm"
                                label={this.appText('modals.reset-password.new-password-confirm')}
                                value={Formatters.getStr(formData, 'resetPasswordConfirm')}
                                type="password"
                                {...inputPropsFullWidthTextDebounced}
                            />

                            <DetailFormCommon.InventoryDetailFormSectionSpacing />

                            <KendoWrappers.KendoCheckbox
                                name="locked"
                                label={this.appText('modals.reset-password.unlock-if-locked-out')}
                                value={_.get(formData, 'locked', false) ? true : false}
                                {...inputProps}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="success"
                        disabled={submitButtonDisabled}
                        onClick={(e) => this.handleCloseModalResetPassword()}
                    >
                        {this.appText('modals.reset-password.set-changes')}
                    </Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalResetPassword()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { textContent, textContentObjId, userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, ...rest } = this.props;

        let pageTitle = this.appTextPageTitleAppendString(userObjId, 'page-title', true);
        let page = (<div />);

        if (Formatters.getStr(textContent, '_id') === textContentObjId) {
            const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, textContent);

            // console.log('pageData', {
            //     user: user,
            //     formData: formData,
            //     numChanges: this.getFormChangesCount(),
            //     formChanges: this.state.formChanges,
            // });

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            pageTitle = this.appTextPageTitleAppendString(Formatters.composeUsernameLabel(user, true), 'page-title.edit', true);
            page = (
                <div>
                    {this.renderResetPasswordModal(formData)}
                    <Row>
                        <Col xs={12} xl={9}>
                            {/* Header toolbar */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar()}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderSectionTextContentDetails(formData)}
                                    </div>
                                </Col>
                            </Row>

                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getTextContentUrl(Formatters.getStr(props, 'match.params.textContentObjId'));

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getTextContentRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    const textContentObjId = Formatters.getStr(props, 'match.params.textContentObjId');

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        textContent: state.entities.textContent,
        textContentObjId: textContentObjId,
    });
};

const mapDispatchToProps = {
    updateUser: MutateActions.updateUserMutateAsync,
    updateLookupObj: MutateActions.updateLookupObjMutateAsync,
    newUser: MutateActions.newUserMutateAsync,
    navigateTo: (path) => push(path),
};

const EditTextContentContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(EditTextContent);

export default withRouter(EditTextContentContainer);



// WEBPACK FOOTER //
// ./src/text-content/edit.tsx